import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Sparkles, Play } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useProduct } from "@/hooks/useProduct";
import { getMeetingSections } from "@/store/slices/meetingSectionSlice";
import { getMeetingModules } from "@/store/slices/meetingModulesSlice";
import {
  selectModuleById,
  selectCategoriesLoading,
} from "@/store/selectors/meetingModulesSelectors";
import { MeetingSessionManager } from "@/utils/meetingSessionManager";
import Paywall from "@/components/Paywall";
import { logger } from "@/utils/logger";

const MeetingCover = () => {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const config = useProduct();

  // Get module data from Redux
  const moduleData = useAppSelector(selectModuleById(meetingId || ""));
  const loading = useAppSelector(selectCategoriesLoading);

  // Fetch meeting modules and sections on mount
  useEffect(() => {
    dispatch(getMeetingModules({}));
  }, [dispatch]);

  useEffect(() => {
    if (meetingId) {
      dispatch(getMeetingSections(meetingId));
    }
  }, [meetingId, dispatch]);

  // Create meeting session UUID when entering this meeting
  useEffect(() => {
    if (meetingId && moduleData) {
      const { module, category } = moduleData;

      // Clear any existing session for this meeting to ensure a fresh UUID
      MeetingSessionManager.clear(meetingId);

      // Create session UUID
      const sessionId = MeetingSessionManager.create(meetingId);

      // Store metadata
      MeetingSessionManager.setMetadata(meetingId, {
        name: module.module_name,
        category: category.name,
      });

      logger.log("[MeetingCover] Created fresh session with metadata:", {
        meetingId,
        sessionId,
        name: module.module_name,
        category: category.name,
      });
    }
    // NOTE: We do NOT clear the session on unmount because user may navigate
    // to other pages within the meeting (prepare, simulation, etc.)
    // The session should persist throughout the entire meeting flow
    // It will only be cleared when truly exiting the meeting (e.g., going back to dashboard)
  }, [meetingId, moduleData]);

  // Loading state
  if (loading && !moduleData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            {config.i18n.meetingCover.loadingDetails}
          </p>
        </div>
      </div>
    );
  }

  // Not found state
  if (!moduleData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">
            {config.i18n.meetingCover.notFound}
          </h1>
          <Link to="/meeting-simulator">
            <Button>{config.i18n.meetingCover.backToAll}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { module, category } = moduleData;

  // Check if module requires paywall
  const requiresPaywall = !module.open_access;

  // If paywall is required, wrap content in Paywall component
  const content = (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/meeting-simulator">
          <Button variant="ghost" className="mb-8 group">
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            {config.i18n.meetingCover.backToAllConversations}
          </Button>
        </Link>

        <div className="animate-fade-in space-y-8">
          <div className="text-center space-y-4">
            <div className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-4">
              {config.i18n.meetingCover.categoryBadge(category.name)}
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-foreground">
              {module.module_name}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {config.i18n.meetingCover.prepareYourself}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-12">
            <Card
              className="group relative overflow-hidden transition-all duration-300 hover:shadow-[var(--shadow-hover)] border-border/50 backdrop-blur-sm bg-card/80 cursor-pointer animate-scale-in"
              onClick={() => navigate(`/meeting/${module.id}/prepare`)}
            >
              <div className="p-8">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Sparkles className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-3">
                  {config.i18n.meetingCover.prepareFirstTitle}
                </h2>
                <p className="text-muted-foreground mb-6">
                  {config.i18n.meetingCover.prepareFirstDescription}
                </p>
                <Button className="w-full group-hover:bg-primary group-hover:text-primary-foreground">
                  {config.i18n.meetingCover.prepareButtonLabel}
                </Button>
              </div>
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
            </Card>

            <Card
              className="group relative overflow-hidden transition-all duration-300 hover:shadow-[var(--shadow-hover)] border-border/50 backdrop-blur-sm bg-card/80 cursor-pointer animate-scale-in"
              onClick={() => navigate(`/meeting/${module.id}/simulate`)}
            >
              <div className="p-8">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Play className="w-6 h-6 text-accent" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-3">
                  {config.i18n.meetingCover.startSimulationTitle}
                </h2>
                <p className="text-muted-foreground mb-6">
                  {config.i18n.meetingCover.startSimulationDescription}
                </p>
                <Button
                  variant="outline"
                  className="w-full group-hover:bg-accent group-hover:text-accent-foreground group-hover:border-accent"
                >
                  {config.i18n.meetingCover.startSimulationButtonLabel}
                </Button>
              </div>
              <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
            </Card>
          </div>
        </div>
      </div>
    </div>
  );

  return requiresPaywall ? (
    <Paywall openAccess={module.open_access}>{content}</Paywall>
  ) : (
    content
  );
};

export default MeetingCover;
